/********************************************************************
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this 
 * file in accordance with the terms of the Adobe license agreement 
 * accompanying it. 
 *******************************************************************/

export type Header = { [key: string]: string | null };

export type FetchOptions = {
  method?: 'GET' | 'POST';
  variables?: { [key: string]: any };
  signal?: AbortSignal;
  cache?:
    | 'default'
    | 'no-store'
    | 'reload'
    | 'no-cache'
    | 'force-cache'
    | 'only-if-cached';
};

export type FetchQueryError = Array<{
  message: string;
  extensions: { category: string };
}>;

export type BeforeHook = (requestInit: RequestInit) => RequestInit;
export type AfterHook<T = any> = (
    requestInit: RequestInit,
    response: { errors?: FetchQueryError; data: T }
) => { errors?: FetchQueryError; data: T };

const defaultHeaders = {
  'Content-Type': 'application/json',
  Accept: 'application/json',
};

class FetchGraphQLMesh {
  protected _endpoint?: string;

  public _fetchGraphQlHeaders: Header = {};

  public _beforeHooks: BeforeHook[] = [];

  public _afterHooks: AfterHook[] = [];

  get endpoint() {
    return this._endpoint;
  }

  get fetchGraphQlHeaders() {
    return this._fetchGraphQlHeaders;
  }

  /**
   * Sets the GraphQL endpoint.
   * @param endpoint - The GraphQL endpoint URL string.
   * @example
   * ```js
   * // Set endpoint as string
   * instance.setEndpoint('https://api.example.com/graphql');
   * ```
   */
  public setEndpoint(endpoint: string) {
    this._endpoint = endpoint;
  }

  /**
   * Sets the GraphQL headers.
   * @param key - The key of the header.
   * @param value - The value of the header.
   */
  public setFetchGraphQlHeader(key: string, value: string | null) {
    this._fetchGraphQlHeaders = {
      ...this._fetchGraphQlHeaders,
      [key]: value,
    };
  }

  /**
   * Removes a specific GraphQL header.
   * @param key - The key of the header.
   */
  public removeFetchGraphQlHeader(key: string) {
    delete this._fetchGraphQlHeaders[key];
  }

  /**
   * Gets the value of a specific GraphQL header.
   * @param key - The key of the header.
   * @returns The value of the header, or undefined if not found.
   */
  public getFetchGraphQlHeader(key: string): string | null | undefined {
    return this._fetchGraphQlHeaders[key];
  }

  /**
   * Sets the GraphQL headers.
   * @param header - The header object or a function that returns a header object.
   * If a function is provided, it will be called with the previous headers.
   * The returned object will be merged with the previous headers.
   * @example
   * ```js
   * // set headers
   * setFetchGraphQlHeaders({ test: 'test' });
   * 
   * // merge with previous headers
   * setFetchGraphQlHeaders((prev) => ({
   *   ...prev,
   *  test: 'test2',
   * }));
   * ```
   */
  public setFetchGraphQlHeaders(header: Header | ((prev: Header) => Header)) {
    if (typeof header === 'function') {
      this._fetchGraphQlHeaders = {
        ...this._fetchGraphQlHeaders,
        ...header(this._fetchGraphQlHeaders),
      };
    } else {
      this._fetchGraphQlHeaders = { ...header };
    }
  }

  /**
   * Adds a hook executed before the GraphQL call.
   * @param hook - The hook function.
   * @example
   * ```js
   * // add before hook
   * addBeforeHook((requestInit) => console.log('About to execute ' + requestInit.method + ' call.'));
   *
   * // modify the requestInit before executing the request
   * addBeforeHook((requestInit) => {method: requestInit.method, body: 'new body'});
   * ```
   */
  public addBeforeHook(hook: BeforeHook): void {
    this._beforeHooks.push(hook);
  }

  /**
   * Adds a hook executed before the GraphQL call.
   * @param hook - The hook function.
   * @example
   * ```js
   * // add before hook
   * addAfterHook((requestInit, response) => console.log(
   *     'The result of ' + requestInit.method + ' call is ' + response.json().body
   * ));
   *
   * // modify the response
   * addAfterHook((requestInit, response) => new Response(JSON.stringify({ ...response, modified: true }));
   * ```
   */
  public addAfterHook(hook: AfterHook): void {
    this._afterHooks.push(hook);
  }
  
  /**
   * Collects all before hooks. Can be overridden by subclasses for inheritance.
   * @protected
   */
  protected _collectBeforeHooks(): BeforeHook[] {
    return this._beforeHooks;
  }
  
  /**
   * Collects all after hooks. Can be overridden by subclasses for inheritance.
   * @protected
   */
  protected _collectAfterHooks(): AfterHook[] {
    return this._afterHooks;
  }
  
  /**
   * Fetches GraphQL data.
   * @param query - The GraphQL query.
   * @param options - Optional configuration for the fetch request.
   * @returns
   */
  public async fetchGraphQl<T = any>(
    query: string,
    options?: FetchOptions
  ): Promise<{ errors?: FetchQueryError; data: T }> {
    const endpoint = this.endpoint;
    const fetchGraphQlHeaders = this.fetchGraphQlHeaders;

    if (!endpoint) throw Error('Missing "url"');

    const method = options?.method ?? 'POST';
    const cache = options?.cache;
    const signal = options?.signal;

    let body;
    const url = new URL(endpoint);
    const headers = {
      ...defaultHeaders,
      ...fetchGraphQlHeaders,
    };

    if (method === 'POST') {
      body = JSON.stringify({
        query,
        variables: options?.variables,
      });
    }

    if (method === 'GET') {
      url.searchParams.append('query', minimizeGraphQlQuery(query));

      if (options?.variables)
        url.searchParams.append('variables', JSON.stringify(options.variables));
    }

    let requestInit: RequestInit = {
      method,
      headers,
      body,
      cache,
      signal,
    };

    // Collect and execute before hooks
    const allBeforeHooks = this._collectBeforeHooks();
    requestInit = await allBeforeHooks.reduce(
        async (prev, hook) => hook(await prev),
        Promise.resolve(requestInit)
    );

    // Collect and execute after hooks
    const allAfterHooks = this._collectAfterHooks();
    return await fetch(url, requestInit).then((r) => r.json().then(
        (response) => allAfterHooks.reduce(
            async (result, hook) => hook(requestInit, await result),
            Promise.resolve(response)
        )
    ));
  }
  
  /**
   * Gets the configuration.
   */
  public getConfig() {
    return {
      endpoint: this.endpoint,
      fetchGraphQlHeaders: this.fetchGraphQlHeaders,
    };
  }

  public getMethods() {
    return {
      setEndpoint: this.setEndpoint.bind(this),
      setFetchGraphQlHeader: this.setFetchGraphQlHeader.bind(this),
      getFetchGraphQlHeader: this.getFetchGraphQlHeader.bind(this),
      removeFetchGraphQlHeader: this.removeFetchGraphQlHeader.bind(this),
      setFetchGraphQlHeaders: this.setFetchGraphQlHeaders.bind(this),
      fetchGraphQl: this.fetchGraphQl.bind(this),
      getConfig: this.getConfig.bind(this),
      addBeforeHook: this.addBeforeHook.bind(this),
      addAfterHook: this.addAfterHook.bind(this),
    };
  }
}

const mesh = new FetchGraphQLMesh();

/**
 * `FetchGraphQL` is a class that extends `FetchGraphQLMesh`.
 * It provides methods to get the GraphQL endpoint and headers with support for inheritance.
 *
 * @class
 *
 */
export class FetchGraphQL extends FetchGraphQLMesh {
  private _mode: 'standalone' | 'linked' | 'default' = 'default';
  private _source?: string | FetchGraphQL;

  get endpoint(): string | undefined {
    switch (this._mode) {
      case 'standalone':
        return this._source as string;
      case 'linked':
        return (this._source as FetchGraphQL)?.endpoint;
      case 'default':
        return mesh.endpoint;
    }
  }

  get fetchGraphQlHeaders(): Header {    
    switch (this._mode) {
      case 'standalone':
        // Standalone instance - only use own headers
        return this._fetchGraphQlHeaders;
      case 'linked':
        // Linked to another instance - use linked instance's headers directly
        return (this._source as FetchGraphQL).fetchGraphQlHeaders;
      case 'default':
        // Default - inherit from global mesh
        return {
          ...mesh.fetchGraphQlHeaders,
          ...this._fetchGraphQlHeaders,
        };
    }
  }

  /**
   * Sets the GraphQL endpoint or links to another FetchGraphQL instance.
   * @param endpoint - The GraphQL endpoint URL string, or a FetchGraphQL instance to link to.
   * @example
   * ```js
   * // Set endpoint as string
   * instance.setEndpoint('https://api.example.com/graphql');
   * 
   * // Link to another instance
   * const parent = new FetchGraphQL();
   * parent.setEndpoint('https://api.example.com/graphql');
   * 
   * const child = new FetchGraphQL();
   * child.setEndpoint(parent); // Links to parent, shares endpoint, headers, and hooks
   * ```
   */
  public setEndpoint(endpoint: string | FetchGraphQL): void {
    if (endpoint instanceof FetchGraphQL) {
      // Link to another instance
      this._mode = 'linked';
      this._source = endpoint;
    } else {
      // Set string endpoint - become standalone
      this._mode = 'standalone';
      this._source = endpoint;
    }
  }

  /**
   * Sets a GraphQL header. When linked to another instance, this sets the header on the linked instance.
   * @param key - The key of the header.
   * @param value - The value of the header.
   */
  public setFetchGraphQlHeader(key: string, value: string | null): void {
    if (this._mode === 'linked') {
      // Delegate to linked instance
      (this._source as FetchGraphQL).setFetchGraphQlHeader(key, value);
    } else {
      // Set on self
      super.setFetchGraphQlHeader(key, value);
    }
  }

  /**
   * Sets the GraphQL headers. When linked to another instance, this sets the headers on the linked instance.
   * @param header - The header object or a function that returns a header object.
   */
  public setFetchGraphQlHeaders(header: Header | ((prev: Header) => Header)): void {
    if (this._mode === 'linked') {
      // Delegate to linked instance
      (this._source as FetchGraphQL).setFetchGraphQlHeaders(header);
    } else {
      // Set on self
      super.setFetchGraphQlHeaders(header);
    }
  }

  /**
   * Removes a specific GraphQL header. When linked to another instance, this removes the header from the linked instance.
   * @param key - The key of the header.
   */
  public removeFetchGraphQlHeader(key: string): void {
    if (this._mode === 'linked') {
      // Delegate to linked instance
      (this._source as FetchGraphQL).removeFetchGraphQlHeader(key);
    } else {
      // Remove from self
      super.removeFetchGraphQlHeader(key);
    }
  }

  /**
   * Gets the value of a specific GraphQL header. When linked to another instance, this gets the header from the linked instance.
   * @param key - The key of the header.
   * @returns The value of the header, or undefined if not found.
   */
  public getFetchGraphQlHeader(key: string): string | null | undefined {
    if (this._mode === 'linked') {
      // Delegate to linked instance
      return (this._source as FetchGraphQL).getFetchGraphQlHeader(key);
    } else {
      // Get from self
      return super.getFetchGraphQlHeader(key);
    }
  }

  /**
   * Adds a before hook. When linked to another instance, this adds the hook to the linked instance.
   * @param hook - The hook function.
   */
  public addBeforeHook(hook: BeforeHook): void {
    if (this._mode === 'linked') {
      // Delegate to linked instance
      (this._source as FetchGraphQL).addBeforeHook(hook);
    } else {
      // Add to self
      super.addBeforeHook(hook);
    }
  }

  /**
   * Adds an after hook. When linked to another instance, this adds the hook to the linked instance.
   * @param hook - The hook function.
   */
  public addAfterHook(hook: AfterHook): void {
    if (this._mode === 'linked') {
      // Delegate to linked instance
      (this._source as FetchGraphQL).addAfterHook(hook);
    } else {
      // Add to self
      super.addAfterHook(hook);
    }
  }

  /**
   * Collects all before hooks. When linked, delegates to the linked instance.
   * @protected
   */
  protected _collectBeforeHooks(): BeforeHook[] {
    if (this._mode === 'linked') {
      return (this._source as FetchGraphQL)._collectBeforeHooks();
    }
    return this._beforeHooks;
  }
  
  /**
   * Collects all after hooks. When linked, delegates to the linked instance.
   * @protected
   */
  protected _collectAfterHooks(): AfterHook[] {
    if (this._mode === 'linked') {
      return (this._source as FetchGraphQL)._collectAfterHooks();
    }
    return this._afterHooks;
  }
}

function minimizeGraphQlQuery(query: string) {
  // Remove comments
  query = query.replace(/#.*/g, '');

  // Remove extra spaces, tabs, and line breaks
  query = query.replace(/\s+/g, ' ');

  return query.trim();
}
/**
 * Exports several methods from the `mesh` object.
 *
 * @property {Function} setEndpoint - Sets the GraphQL endpoint.
 * @property {Function} setFetchGraphQlHeaders - Sets the GraphQL headers.
 * @property {Function} setFetchGraphQlHeader - Sets a specific GraphQL header.
 * @property {Function} getFetchGraphQlHeader - Gets the value of a specific GraphQL header.
 * @property {Function} removeFetchGraphQlHeader - Removes a specific GraphQL header.
 * @property {Function} fetchGraphQl - Fetches GraphQL data.
 * @property {Function} getConfig - Gets the configuration.
 * @property {Function} addBeforeHook - Adds a hook executed before the GraphQL call.
 * @property {Function} addAfterHook - Adds a hook executed after the GraphQL call.
 */

// Global Mesh instance
export const {
  setEndpoint,
  setFetchGraphQlHeaders,
  setFetchGraphQlHeader,
  getFetchGraphQlHeader,
  removeFetchGraphQlHeader,
  fetchGraphQl,
  getConfig,
  addBeforeHook,
  addAfterHook,
} = mesh.getMethods();
